import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  LinearProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useApi } from "../../utils/api";
import LoadingButton from "@mui/lab/LoadingButton";

const UploadDocumentModal = ({ open, onClose, clientId, clientName }) => {
  const {
    getClientDocuments,
    uploadClientDocument,
    deleteClientDocument,
    putSignedUrl,
    getSignedUrl,
  } = useApi();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState("general");
  const [deletingId, setDeletingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const documentTypes = [
    { value: "general", label: "General" },
    { value: "liability-waiver", label: "Liability Waiver" },
    { value: "questionnaire", label: "Questionnaire" },
    { value: "contract", label: "Contract" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    if (open && clientId) {
      fetchDocuments();
    }
  }, [open, clientId]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const docs = await getClientDocuments(clientId);
      setDocuments(docs || []);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
      setError("Failed to load documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const allowedExtensions = [".pdf", ".doc", ".docx"];

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    const isValidType =
      allowedTypes.includes(file.type) ||
      allowedExtensions.includes(fileExtension);

    if (!isValidType) {
      setError(
        "Please select a PDF or Word document (.pdf, .doc, .docx files only)"
      );
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !clientId) {
      setError("Please select a file");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Generate unique key for S3
      const timestamp = Date.now();
      const fileName = `${timestamp}-${selectedFile.name}`;
      const key = `client-documents/${clientId}/${fileName}`;

      // Get presigned URL
      const { uploadUrl } = await putSignedUrl(key);

      // Upload file to S3 with progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader("Content-Type", selectedFile.type);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100
            );
            setUploadProgress(percentComplete);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error("Network error during upload"));
        };

        xhr.send(selectedFile);
      });

      // Save document metadata
      await uploadClientDocument({
        clientId,
        fileName: selectedFile.name,
        s3Key: key,
        documentType,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
      });

      // Reset form and refresh list
      setSelectedFile(null);
      setDocumentType("general");
      setUploadProgress(0);
      await fetchDocuments();
    } catch (err) {
      console.error("Failed to upload document:", err);
      setError(err.message || "Failed to upload document. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (doc) => {
    setViewingId(doc.documentId);
    try {
      // Get signed URL for viewing/downloading
      const response = await getSignedUrl(doc.s3Key);
      const signedUrl = response.downloadUrl || response.url || response;

      // Open in new tab for PDFs, download for Word docs
      if (doc.mimeType === "application/pdf") {
        window.open(signedUrl, "_blank");
      } else {
        // For Word docs, trigger download or open in new tab
        const link = document.createElement("a");
        link.href = signedUrl;
        link.download = doc.fileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error("Failed to get document URL:", err);
      setError("Failed to open document. Please try again.");
    } finally {
      setViewingId(null);
    }
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setDeleteConfirmOpen(false);
    setDeletingId(documentToDelete.documentId);
    try {
      await deleteClientDocument(documentToDelete.documentId, clientId);
      await fetchDocuments();
    } catch (err) {
      console.error("Failed to delete document:", err);
      setError("Failed to delete document. Please try again.");
    } finally {
      setDeletingId(null);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDocumentTypeLabel = (type) => {
    const docType = documentTypes.find((dt) => dt.value === type);
    return docType ? docType.label : type;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Documents for {clientName || "Client"}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Upload Section */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Upload New Document
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                label="Document Type"
                disabled={uploading}
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              component="label"
              disabled={uploading}
              startIcon={<DescriptionIcon />}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileSelect}
              />
            </Button>

            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedFile.name} (
                {formatFileSize(selectedFile.size)})
              </Typography>
            )}

            {uploading && (
              <Box>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            <LoadingButton
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              loading={uploading}
            >
              Upload Document
            </LoadingButton>
          </Box>
        </Box>

        {/* Documents List */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Existing Documents ({documents.length})
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No documents uploaded yet.
            </Typography>
          ) : (
            <List>
              {documents.map((doc) => (
                <ListItem
                  key={doc.documentId}
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleView(doc)}
                >
                  <DescriptionIcon sx={{ mr: 2, color: "text.secondary" }} />
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          "&:hover": {
                            textDecoration: "underline",
                            color: "primary.main",
                          },
                        }}
                      >
                        {doc.fileName}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Chip
                          label={getDocumentTypeLabel(doc.documentType)}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {formatFileSize(doc.fileSize || 0)} •{" "}
                          {formatDate(doc.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(doc);
                      }}
                      disabled={viewingId === doc.documentId}
                      sx={{ mr: 1 }}
                      title="View document"
                    >
                      {viewingId === doc.documentId ? (
                        <CircularProgress size={20} />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(doc);
                      }}
                      disabled={deletingId === doc.documentId}
                      title="Delete document"
                    >
                      {deletingId === doc.documentId ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{documentToDelete?.fileName}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UploadDocumentModal;
